import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import invariant from "invariant";
import Icon from "../icons/Icon";
import { createDataAttributes } from "../../utilities";
import "./Button.scss";

const Button = ({
  block,
  children,
  className,
  dataAttrib,
  dataWrapperAttrib,
  disabled,
  icon,
  iconPos,
  inline,
  labelSpace,
  onClick,
  size,
  spinner,
  spinnerPos,
  type,
  variant,
  wide,
  wrapperClassName,
  isLink,
  tabIndex,
  ...rest
}) => {
  invariant(
    !!icon || !!children,
    "Button content or an icon is required for a button."
  );

  const containerClasses = classnames("uikit__btn", wrapperClassName, {
    uikit__btn__disabled: disabled,
    uikit__btn__block: block,
    uikit__btn__labelSpace: labelSpace
  });

  const buttonClasses = classnames(
    "uikit__btn__button",
    "uikit__btn",
    className,
    {
      uikit__btn__small: size === "sm",
      uikit__btn__large: size === "lg",
      uikit__btn__wide: wide,
      "uikit__btn__with-icon": icon,
      uikit__btn__inline: inline,
      [`uikit_btn-${variant}`]: variant,
      "uikit__btn__icon-only": icon && !children,
      "uikit__btn-link": isLink
    }
  );

  return (
    <span
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      <button
        type={type}
        className={buttonClasses}
        disabled={disabled}
        onClick={onClick}
        tabIndex={tabIndex}
        {...createDataAttributes(dataAttrib)}
        {...rest}
      >
        {spinner && spinnerPos === "left" ? (
          <span className="uikit__btn__spinner uikit__btn__spinner-left">
            <Icon icon="farSpinnerThird" />
          </span>
        ) : null}
        {icon ? (
          <span
            className={classnames("uikit__icon-wrapper", "uikit__btn__icon", {
              [`uikit__btn__icon-pos-${iconPos}`]: icon
            })}
          >
            {React.isValidElement(icon) ? icon : <Icon icon={icon} />}
          </span>
        ) : null}
        {children && <span className="uikit__btn__text">{children}</span>}
        {spinner && spinnerPos === "right" ? (
          <span className="uikit__btn__spinner uikit__btn__spinner-right">
            <Icon icon="farSpinnerThird" />
          </span>
        ) : null}
      </button>
    </span>
  );
};

Button.propTypes = {
  block: propTypes.bool,
  children: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node
  ]),
  className: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  disabled: propTypes.bool,
  icon: propTypes.oneOfType([propTypes.string, propTypes.element]),
  iconPos: propTypes.oneOf(["left", "right"]),
  inline: propTypes.bool,
  labelSpace: propTypes.bool,
  onClick: propTypes.func,
  size: propTypes.oneOf(["sm", "md", "lg"]),
  spinner: propTypes.bool,
  spinnerPos: propTypes.oneOf(["left", "right"]),
  type: propTypes.oneOf(["button", "submit", "reset"]),
  variant: propTypes.oneOf([
    "info",
    "link",
    "plain",
    "primary",
    "secondary",
    "tertiary",
    "outline"
  ]),
  wide: propTypes.bool,
  wrapperClassName: propTypes.string,
  isLink: propTypes.bool,
  tabIndex: propTypes.number
};

Button.defaultProps = {
  className: "",
  disabled: false,
  labelSpace: false,
  size: "md",
  spinnerPos: "left",
  type: "button",
  wide: false,
  isLink: false,
  variant: "plain",
  tabIndex: 0,
  iconPos: "left"
};

export default Button;
